import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Product } from "@/types/product";
import { optimizeImageUrl } from "@/lib/image-utils";

interface Props {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AvailabilityInquiryModal = ({ product, open, onOpenChange }: Props) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("Labdien! Vai šī prece būs pieejama drīzumā?");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const primaryImage = product.images?.[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Lūdzu ievadiet derīgu e-pasta adresi");
      return;
    }

    setSending(true);
    try {
      const productUrl = `${window.location.origin}/${product.slug}`;

      // Save to DB
      const { error: dbError } = await supabase.from("availability_inquiries" as any).insert({
        product_id: product.id,
        product_name: product.name,
        product_url: productUrl,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        message: message.trim() || null,
      } as any);

      if (dbError) throw dbError;

      // Send notification email
      try {
        await supabase.functions.invoke("send-contact-email", {
          body: {
            name: name.trim(),
            email: email.trim(),
            subject: `Pieejamības pieprasījums — ${product.name}`,
            message: [
              `Produkts: ${product.name}`,
              `Zīmols: ${product.brand}`,
              `SKU: ${product.sku || "N/A"}`,
              `Cena: €${product.price.toFixed(2)}`,
              `Saite: ${productUrl}`,
              ``,
              `Telefons: ${phone.trim() || "Nav norādīts"}`,
              ``,
              `Ziņa:`,
              message.trim() || "(Nav ziņas)",
            ].join("\n"),
          },
        });
      } catch {
        // Email send failed but DB record was saved — acceptable
      }

      setSent(true);
      setTimeout(() => {
        onOpenChange(false);
        // Reset form after close
        setTimeout(() => {
          setSent(false);
          setName("");
          setEmail("");
          setPhone("");
          setMessage("Labdien! Vai šī prece būs pieejama drīzumā?");
        }, 300);
      }, 1500);
    } catch (err) {
      console.error("Inquiry submit error:", err);
      toast.error("Kļūda nosūtot pieprasījumu. Lūdzu mēģiniet vēlreiz.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Sazināties par pieejamību</DialogTitle>
        </DialogHeader>

        {sent ? (
          <div className="text-center py-8">
            <p className="text-lg font-display font-semibold text-primary mb-2">Paldies!</p>
            <p className="text-sm text-muted-foreground">Mēs sazināsimies ar jums drīz.</p>
          </div>
        ) : (
          <>
            {/* Product summary */}
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg mb-2">
              {primaryImage && (
                <img
                  src={optimizeImageUrl(primaryImage, { width: 80 })}
                  alt={product.name}
                  className="w-14 h-14 object-cover rounded-md"
                  width={56}
                  height={56}
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">{product.brand}</p>
                <p className="text-sm font-medium leading-tight line-clamp-2">{product.name}</p>
                <p className="text-sm font-display font-bold text-price">€{product.price.toFixed(2)}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="inquiry-name">Vārds *</Label>
                <Input id="inquiry-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jūsu vārds" />
              </div>
              <div>
                <Label htmlFor="inquiry-email">E-pasts *</Label>
                <Input id="inquiry-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="jusu@epasts.lv" />
              </div>
              <div>
                <Label htmlFor="inquiry-phone">Telefons</Label>
                <Input id="inquiry-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+371 ..." />
              </div>
              <div>
                <Label htmlFor="inquiry-message">Ziņa</Label>
                <Textarea id="inquiry-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} />
              </div>
              <Button type="submit" className="w-full font-display" disabled={sending}>
                {sending ? "Sūta..." : "Nosūtīt pieprasījumu"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AvailabilityInquiryModal;
